import { UnlinkedSessionIdAtom } from '@/components/modal/unlink-session-from-patient-modal';
import { PhoneInput } from '@/components/ui/phone-input';
import { TrackedButton } from '@/components/ui/tracked-button';
import { IS_INSET_DESIGN_ENABLED, useIsSessionUrgencyEnabled } from '@/features/flags';
import { PERMISSION_SUBJECTS } from '@/helpers/casl/ability';
import { isSessionPatientLinkingReadOnly } from '@/hooks/integrations/isSessionPatientLinkingReadOnly';
import { useIsEhrLaunch } from '@/hooks/integrations/useIsEhrLaunch';
import { useMutateDeletePatientProfile } from '@/hooks/mutation/useMutateDeletePatientProfile';
import { useMutateUnlinkSessionsToPatientProfile } from '@/hooks/mutation/useMutateUnlinkSessionsToPatientProfile';
import { useMutateUpdatePatientProfile } from '@/hooks/mutation/useMutateUpdatePatientProfile';
import { useMutateUploadPatientDocuments } from '@/hooks/mutation/useMutateUploadPatientDocuments';
import { useQueryPatientDocumentDownloadUrl } from '@/hooks/query/useQueryPatientDocumentDownloadUrl';
import { useQueryPatientDocuments } from '@/hooks/query/useQueryPatientDocuments';
import { useQueryPatientProfiles } from '@/hooks/query/useQueryPatientProfiles';
import { useQueryPatientRecap } from '@/hooks/query/useQueryPatientRecap';
import { useFormatSessionTime } from '@/hooks/useFormatSessionTime';
import { useRBACPermissionCheck } from '@/hooks/useRBACPermissionCheck';
import { ReactPdfDocument, ReactPdfPage } from '@/lib/pdf/react-pdf-loader';
import { trackPatientUpdated } from '@/models/analytics/features/trackPatientProfileUpdateAnalytics';
import { trackPatientRecapSourceSessionClicked } from '@/models/analytics/features/trackPatientRecapAnalytics';
import { artifactFieldUpdateAtom } from '@heidi/agents/data';
import { useUserTier } from '@heidi/commerce/data';
import {
  PatientInputField,
  PatientTextareaField,
  usePatientProfileDeleteContext,
  type EmitPatientSurfaceEvent,
  type PatientProfileHost,
  type UseForeignSessionGuard,
} from '@heidi/patients';
import {
  buildSessionStatusOptions,
  isSessionUrgent,
  sessionUrgencyMessages,
} from '@heidi/session';
import { SESSION_SUMMARY_FALL_BACK } from '@heidi/session/contracts';
import {
  useQueryDrawerLinkedSessions,
  useQueryForeignSessionOwners,
  useQuerySessionById,
} from '@heidi/session/data';
import { SettingsSectionCard } from '@heidi/snowflake-ui/section-card';
import { useUser } from '@heidi/workspace/data';
import { useIsSharedPatientDirectoryEnabled } from '@heidi/workspace/flags';
import { useAtomValue, useSetAtom } from 'jotai';
import { useRouter } from 'next/router';

import { usePatientContextFieldsHost } from '../patient-context/usePatientContextFieldsHost';
import { emitPatientSurfaceEvent } from './analytics/emit';
import { resolvePatientSurfaceFlagVariant } from './analytics/flag-variant';
import { usePatientDocumentSizeGuard } from './use-patient-document-size-guard';
import { useCreatePatientSession } from './useCreatePatientSession';
import { useIsSessionEhrReadOnly } from './useIsSessionEhrReadOnly';
import { useOpenPatientFullPage } from './useOpenPatientFullPage';

/** Stamps the patient-list cohort, then emits. One callback for both. */
export function emitPatientProfileSurfaceEvent(
  event: Parameters<EmitPatientSurfaceEvent>[0]
): void {
  const feature_flag_variant = resolvePatientSurfaceFlagVariant();
  switch (event.event_name) {
    case 'patient_list_viewed':
      emitPatientSurfaceEvent({
        event_name: event.event_name,
        props: { ...event.props, feature_flag_variant },
      });
      return;
    case 'patient_profile_opened':
      emitPatientSurfaceEvent({
        event_name: event.event_name,
        props: { ...event.props, feature_flag_variant },
      });
      return;
    case 'patient_profile_tab_viewed':
      emitPatientSurfaceEvent({
        event_name: event.event_name,
        props: { ...event.props, feature_flag_variant },
      });
      return;
    case 'patient_session_expanded':
      emitPatientSurfaceEvent({
        event_name: event.event_name,
        props: { ...event.props, feature_flag_variant },
      });
      return;
    case 'patient_field_edited':
      emitPatientSurfaceEvent({
        event_name: event.event_name,
        props: { ...event.props, feature_flag_variant },
      });
      return;
    case 'patient_document_uploaded':
      emitPatientSurfaceEvent({
        event_name: event.event_name,
        props: { ...event.props, feature_flag_variant },
      });
      return;
    case 'patient_document_linked':
      emitPatientSurfaceEvent({
        event_name: event.event_name,
        props: { ...event.props, feature_flag_variant },
      });
      return;
    case 'patient_document_unlinked':
      emitPatientSurfaceEvent({
        event_name: event.event_name,
        props: { ...event.props, feature_flag_variant },
      });
      return;
    case 'patient_recap_popover_opened':
      emitPatientSurfaceEvent({
        event_name: event.event_name,
        props: { ...event.props, feature_flag_variant },
      });
      return;
  }
}

/** RBAC plus the two CASL subjects the danger zone used to read itself. */
export function useCanDeletePatient(): {
  isReviewingOtherUser: boolean;
  canDeleteTeamSessions: boolean;
} {
  const { isReviewingOtherUser, checkRBACPermission } = useRBACPermissionCheck();
  return {
    isReviewingOtherUser,
    canDeleteTeamSessions: checkRBACPermission(
      PERMISSION_SUBJECTS.TEAM_OTHERS_SESSION_DELETE,
      PERMISSION_SUBJECTS.SESSION_DELETE
    ),
  };
}

function routeQueryValue(value: string | string[] | undefined): string | null {
  return typeof value === 'string' ? value : null;
}

/** Binds the patient-profile surface to the app implementations it renders. */
export function usePatientProfileHost(): PatientProfileHost {
  const router = useRouter();
  const fieldsHost = usePatientContextFieldsHost();
  const artifactFieldUpdate = useAtomValue(artifactFieldUpdateAtom);
  const setUnlinkedSessionId = useSetAtom(UnlinkedSessionIdAtom);
  const isSessionUrgencyEnabled = useIsSessionUrgencyEnabled();
  const isSharedPatientDirectoryEnabled = useIsSharedPatientDirectoryEnabled();
  // COLAB-394 foreign-session scan port. The drawer flow filters ids to the
  // permitted subset and gates on open; the admin + flag-gate branches live
  // inside the hook body (called by the component, not the host builder) so
  // the host-builder test runs without a QueryClientProvider.
  // `kinde_user_id` NOT `user_ref_id` — a session's `kinde_gp_id` is Kinde.
  const useForeignSessionGuard: UseForeignSessionGuard = ({ patientProfileIds }) => {
    const context = usePatientProfileDeleteContext(isSharedPatientDirectoryEnabled);
    const { data: currentKindeUserId } = useUser((d) => d?.kinde_user_id);
    return useQueryForeignSessionOwners({
      patientProfileIds,
      currentKindeUserId,
      enabled:
        context.isSharedDirectoryEnabled === true &&
        !context.isWorkspaceAdmin &&
        patientProfileIds.length > 0,
    });
  };

  return {
    useMutateUpdatePatientProfile,
    useMutateUploadPatientDocuments,
    useMutateDeletePatientProfile,
    useMutateUnlinkSessionsToPatientProfile,
    useQueryPatientProfiles,
    useQueryPatientDocuments,
    useQueryPatientDocumentDownloadUrl,
    useQueryPatientRecap,
    useQueryDrawerLinkedSessions,
    useQuerySessionById,
    useIsEhrLaunch,
    useIsSessionEhrReadOnly,
    isSessionPatientLinkingReadOnly,
    useCanDeletePatient,
    useForeignSessionGuard,
    useCreatePatientSession,
    useOpenPatientFullPage,
    useGuardFileSize: usePatientDocumentSizeGuard,
    onNavigate: (href) => {
      void router.push(href);
    },
    profileRoute: {
      from: routeQueryValue(router.query.from),
      sessionId: routeQueryValue(router.query.sessionId),
      fromPath: routeQueryValue(router.query.fromPath),
    },
    PhoneInput,
    SectionCard: SettingsSectionCard,
    TrackedButton,
    PatientInputField,
    PatientTextareaField,
    ReactPdfDocument,
    ReactPdfPage,
    buildSessionStatusOptions,
    sessionUrgencyMessages,
    isSessionUrgent,
    useFormatSessionTime,
    sessionSummaryFallback: SESSION_SUMMARY_FALL_BACK,
    isSessionUrgencyEnabled,
    isSharedPatientDirectoryEnabled,
    useUserTier,
    artifactFieldUpdate,
    setUnlinkedSessionId,
    isInsetDesign: IS_INSET_DESIGN_ENABLED,
    trackPatientRecapSourceSessionClicked,
    trackPatientUpdated,
    emitSurfaceEvent: emitPatientProfileSurfaceEvent,
    fieldsHost,
  };
}
